import { timestamp } from 'rxjs/operators';
import { Platform } from '@ionic/angular';
import { FirebaseX } from '@ionic-native/firebase-x/ngx';
import { Injectable } from '@angular/core';
import { CrashService } from './crash.service';
import { Subscription } from 'rxjs';
import { AuthService, User } from './auth.service';
import { CodeResponse } from './sms-code.service';

interface AnalyticsProperties {
  source?: string; // source component a qt_user_* event was triggered in
  error?: string;
  input?: string;
  reason?: string;
  member_type?: string;
  member_status?: string;
  member_id?: string;
  staff_status?: string;
  staff_id?: string;
  mobile?: string;
  media_type?: string;
  template?: string;
  response?: CodeResponse;
  team_id?: string;
  message_id?: string;
  subscription?: string;
  value?: string;
  destination?: string;
  country?: string;
  analyticsId?: string;
  state?: boolean; // A logic state of note
  ui?: boolean; // Was the event triggered directly by a user interaction?
  crash?: boolean; // Report the event to Crashlytics?
  blocked?: boolean; // Was the user action blocked due to IAP requirements?
  fatal?: boolean; // Report the event to Crashlytics as Fatal or Non-Fatal (crash must also be true)
  caused_by?: string; // Name of the component or service that triggered the event (hyphen-case)
  in_func?: string; // The name of the function that triggered it
  properties?: AnalyticsProperties;
  msg?: string; // description of the issue
  data?: AnalyticsProperties; // any relevant data to be logged to the crash report
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  userDataSubscription: Subscription;
  analyticsId = 'unknown';
  debug = true;

  constructor(
    private firebaseX: FirebaseX,
    private platform: Platform,
    private crash: CrashService,
    private auth: AuthService,
  ) {
    this.setUserId('unknown');
    this.userDataSubscription = this.auth.userData$.subscribe(
      async (userData) => {
        if (userData) {
          if (userData.analyticsId !== this.analyticsId) {
            this.log('Analytics Service detected user change');
            this.analyticsId = userData.analyticsId;
          } else {
            this.log('Analytics Service says no user change');
          }
        } else {
          this.log('Analytics Service got no user');
        }
        this.setUserId(this.analyticsId);
      },
      (error: any) => {
        this.crash.subscription(
          'analytics-service',
          'userDataSubscription',
          error,
        );
      },
    );
  }

  // JL_TODO: track hamburger menu hits

  track(name: string, properties: AnalyticsProperties = {}) {
    this.log('Analytics track', { name, properties });
    properties['timestamp'] = new Date().toISOString();
    properties['analytics_id'] = this.analyticsId;
    this.platform.ready().then(
      (readySource) => {
        if (readySource === 'cordova') {
          this.firebaseX.logEvent(name, properties);
          if (properties.ui) {
            this.firebaseX.logEvent(name, properties);
          }
        }
        // else {
        //   this.log('Running in browser, ignore analytics send');
        // }
      },
      (err) => {
        this.crash.process(
          'analytics-service',
          'logEvent',
          'Failed to log event',
          { name, properties, err },
        );
      },
    );
  }

  /**
   * Tracks a custom event or crash in Firebase Analytics/Crashlytics
   *
   * @param type The type of occurence to track: crash | event
   * @param navigationExtras Options that control the navigation strategy. This function
   * only utilizes properties in `NavigationExtras` that would change the provided URL.
   * @returns The new URL tree.
   *
   * @usageNotes
   *
   */
  // trackEvent(name: string, properties = {}) {
  //   this.log('Analytics trackEvent', { name, properties });
  //   properties['timestamp'] = new Date().toISOString();
  //   this.platform.ready().then(
  //     (readySource) => {
  //       if (readySource === 'cordova') {
  //         this.firebaseX.logEvent(name, properties);
  //       }
  //       // else {
  //       //   this.log('Running in browser, ignore analytics send');
  //       // }
  //     },
  //     (err) => {
  //       this.crash.process(
  //         'analytics-service',
  //         'logEvent',
  //         'Failed to log event',
  //         { name, properties, err },
  //       );
  //     },
  //   );
  // }

  // error(name: string, source string, in_func: string, properties: AnalyticsProperties = {}) {
  //   this.log('Analytics trackError', { name, caused_by, in_func, properties });
  //   const timestamp = new Date().toISOString();
  //   if (properties) {
  //     properties['timestamp'] = timestamp;
  //   } else {
  //     properties = { timestamp };
  //   }
  //   this.platform.ready().then(
  //     (readySource) => {
  //       if (readySource === 'cordova') {
  //         this.firebaseX.logEvent(name, properties);
  //       }
  //       // else {
  //       //   this.log('Running in browser, ignore analytics send');
  //       // }
  //     },
  //     (err) => {
  //       this.crash.process(
  //         'analytics-service',
  //         'logEvent',
  //         'Failed to log event',
  //         { name, properties, err },
  //       );
  //     },
  //   );
  // }

  setUserProperty(key: string, value: string) {
    this.log('Analytics setUserProperty', { key, value });
    this.platform.ready().then(
      (readySource) => {
        if (readySource === 'cordova') {
          this.firebaseX.setUserProperty(key, value);
        }
        // else {
        //   this.log('Running in browser, ignore analytics send');
        // }
      },
      (err) => {
        this.crash.process(
          'analytics-service',
          'setUserProperty',
          'Failed to set property',
          { key, value, err },
        );
      },
    );
  }

  // Tracks a 'screen_view' event in Firebase Analytics
  trackScreen(name: string) {
    this.log('Analytics trackScreen', name);
    this.platform.ready().then(
      (readySource) => {
        if (readySource === 'cordova') {
          this.firebaseX.setScreenName(name);
        }
        // else {
        //   this.log('Running in browser, ignore analytics send');
        // }
      },
      (err) => {
        this.crash.process(
          'analytics-service',
          'trackScreen',
          'Failed to track screen',
          { name, err },
        );
      },
    );
  }

  setUserId(analyticsId: string) {
    this.log('Analytics setUserId', { analyticsId });
    this.platform.ready().then(
      (readySource) => {
        if (readySource === 'cordova') {
          this.firebaseX.setUserId(analyticsId);
        }
        // else {
        //   this.log('Running in browser, ignore analytics send');
        // }
      },
      (err) => {
        this.crash.process(
          'analytics-service',
          'setUserId',
          'Failed to set user id',
          { analyticsId, err },
        );
      },
    );
  }

  log(msg, data?) {
    if (this.debug) {
      if (data) {
        console.warn(msg, data);
      } else {
        console.warn(msg);
      }
    }
  }
}
